import { usePracticeSupportSettings } from 'lib/hooks/queries/practice/usePracticeSupportSettings';
import { useGetWizardInstance } from 'lib/hooks/queries/wizard/useGetWizardInstance';

import { useAssignedTasks } from './useAssignedTasks';

/**
 * Bundles the queries Stepper needs: wizard instance, practice support settings,
 * and tasks assigned to the current user.
 *
 * When `wizardStateOverride` is provided (checkin / instant packet fetch and
 * normalize their instance via a different endpoint; self-scheduling runs
 * unauthenticated), every authenticated query here is skipped and the supplied
 * state is returned instead, so the caller sees the same shape regardless of
 * mode. Skipping also avoids 401s on support_settings / wizard_delegations that
 * would otherwise redirect to /login via the axios interceptor.
 */
export default function useStepperData({
  practice,
  wizardKind,
  currentUserId,
  wizardStateOverride = null
}) {
  const skipAuthQueries = wizardStateOverride != null;

  const { data: supportData } = usePracticeSupportSettings({
    params: { practiceId: practice?.id },
    dependencies: [practice?.id],
    options: { enabled: !!practice?.id && !skipAuthQueries }
  });

  const { data, isLoading: queryLoading } = useGetWizardInstance({
    params: { practice_id: practice?.id, wizard_kind_code: wizardKind },
    dependencies: [practice?.id, wizardKind],
    options: { enabled: !!practice?.id && !skipAuthQueries }
  });

  const wizardState = wizardStateOverride ?? data?.data;
  const wizardInstanceId = wizardState?.instance?.id;
  const isLoading = skipAuthQueries ? false : queryLoading;

  const { assignedTasks, delegations } = useAssignedTasks({
    instanceId: skipAuthQueries ? null : wizardInstanceId,
    practiceId: skipAuthQueries ? null : practice?.id,
    currentUserId
  });

  return {
    isLoading,
    wizardState,
    wizardInstanceId,
    consultant: supportData?.practiceSupport?.consultant,
    assignedTasks,
    delegations
  };
}
